import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Search } from "lucide-react";
import { LGoButton, LGoFormField, LGoTextField } from "@/ui/branded";
import { RESERVATION_CODE_LENGTH, normalizeReservationCode } from "@/domain/reservation/rules";
import { codeLookupSchema, type CodeLookupValues } from "@/features/validate/form-schema";

interface CodeLookupFormProps {
  code: string;
  onSubmit: (code: string) => void;
  onCodeInFieldChange: (codeInField: string) => void;
  isSearching: boolean;
}

export function CodeLookupForm({
  code,
  onSubmit,
  onCodeInFieldChange,
  isSearching,
}: CodeLookupFormProps) {
  const form = useForm<CodeLookupValues>({
    resolver: zodResolver(codeLookupSchema),
    defaultValues: { code },
  });

  const submit = form.handleSubmit((values) => onSubmit(values.code));

  return (
    <FormProvider {...form}>
      <form onSubmit={submit} className="flex w-full max-w-sm flex-col gap-3">
        <LGoFormField name="code" label="Kód rezervace">
          {(field) => (
            <LGoTextField
              name={field.name}
              value={String(field.value ?? "")}
              // Clamped here rather than with maxLength because the browser
              // truncates a paste before any handler sees it: „k7 m2 p" pasted
              // into a five-character input arrives as „k7 m2" and the partner
              // is refused a code they hold correctly.
              onChange={(event) => {
                const codeInField = normalizeReservationCode(event.target.value).slice(
                  0,
                  RESERVATION_CODE_LENGTH,
                );
                field.onChange(codeInField);
                onCodeInFieldChange(codeInField);
              }}
              onBlur={field.onBlur}
              placeholder="K7M2P"
              aria-label="Kód rezervace"
              autoFocus
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              fieldClassName="h-16 [&_input]:h-16 [&_input]:text-center [&_input]:indent-[0.35em] [&_input]:text-3xl [&_input]:font-semibold [&_input]:uppercase [&_input]:tracking-[0.35em]"
            />
          )}
        </LGoFormField>
        <LGoButton
          type="submit"
          size="lg"
          iconLeft={<Search />}
          loading={isSearching}
          className="w-full"
        >
          Najít rezervaci
        </LGoButton>
      </form>
    </FormProvider>
  );
}
